import ActionButton from "@/components/buttons/ActionButton";
import HiddenInput from "@/components/form/components/HiddenInput";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import AntIcon from "@/components/icons/AntIcon";
import { InputNumber } from "@/components/InputNumber";
import { datePickerFormItemProps, datePickerStandardProps, selectStandardProps } from "@/utils/componentUtils";
import { formatLocaleNumber } from "@/utils/formatUtils";
import { dateToIsoDateString, isDefined, numberOrZero, toDate } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import {
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Divider,
  Form,
  FormInstance,
  FormListFieldData,
  Row,
  Select,
  Tabs,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { TFunction } from "i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { rowGutter } from "../../../../../common/constants";
import { CreateUpdateEntries } from "../../../../../common/types";
import { useIsCurrentAgentTypePlus } from "../../../../auth/hooks";
import { CommissionKind, CommissionKindCategory } from "../../../batch/enums";
import { getCommissionKindCategory } from "../../../batch/utils";
import { CommissionDivisionType } from "../../enum";
import { CreateUpdateProductCommissionsSettings } from "../../types";
import { getCommissionKindTabLabel, getProductCommissionsSettingsLabel } from "../../utils";
import { AddCommissionKindTabModal } from "../AddCommissionKindTabModal";

type TargetKey = React.MouseEvent | React.KeyboardEvent | string;

type DivisionTypeFormItems = {
  index: number;
  divisionType?: CommissionDivisionType;
  year1PaymentRatio?: number;
  year2PaymentRatio?: number;
  year3PaymentRatio?: number;
};

const DivisionTypeFormItems = ({
  index,
  divisionType,
  year1PaymentRatio,
  year2PaymentRatio,
  year3PaymentRatio,
}: DivisionTypeFormItems) => {
  const { t } = useTranslation();

  const form = Form.useFormInstance<CreateUpdateEntries<CreateUpdateProductCommissionsSettings>>();
  const entries = Form.useWatch("entries", { form, preserve: true });

  useEffect(() => {
    if (divisionType === CommissionDivisionType.NONE) {
      form.setFieldsValue({
        entries: {
          ...entries,
          [index]: { year1PaymentRatio: undefined, year2PaymentRatio: undefined, year3PaymentRatio: undefined },
        },
      });
    }

    if (divisionType === CommissionDivisionType.TWO_YEARS) {
      form.setFieldsValue({
        entries: {
          ...entries,
          [index]: { year3PaymentRatio: undefined },
        },
      });
    }
  }, [divisionType, entries]);

  return (
    <Row gutter={rowGutter}>
      <Col span={6}>
        <Form.Item
          name={[index, "divisionType"]}
          label={t("commissions.productCommissionsSettings.enums.commissionDivisionType._label")}
          rules={[validations.notNull]}
        >
          <Select
            {...selectStandardProps}
            options={Object.keys(CommissionDivisionType).map((type) => ({
              value: type,
              label: t("commissions.productCommissionsSettings.enums.commissionDivisionType." + type),
            }))}
          />
        </Form.Item>
      </Col>

      {divisionType && divisionType !== CommissionDivisionType.NONE ? (
        <Col span={6}>
          <Form.Item
            name={[index, "year1PaymentRatio"]}
            label={t("commissions.productCommissionsSettings.attrs.year1PaymentRatio")}
            rules={[
              validations.notNull,
              validations.minNumber(0.01),
              validations.maxNumber(100),
              validations.sumOneHundred([year1PaymentRatio, year2PaymentRatio, year3PaymentRatio]),
            ]}
            dependencies={
              divisionType === CommissionDivisionType.THREE_YEARS
                ? [
                    ["entries", index, "year2PaymentRatio"],
                    ["entries", index, "year3PaymentRatio"],
                  ]
                : [["entries", index, "year2PaymentRatio"]]
            }
          >
            <InputNumber type="decimal" addon="percentage" />
          </Form.Item>
        </Col>
      ) : undefined}

      {divisionType === CommissionDivisionType.TWO_YEARS || divisionType === CommissionDivisionType.THREE_YEARS ? (
        <Col span={6}>
          <Form.Item
            name={[index, "year2PaymentRatio"]}
            label={t("commissions.productCommissionsSettings.attrs.year2PaymentRatio")}
            rules={[
              validations.notNull,
              validations.minNumber(0.01),
              validations.maxNumber(100),
              validations.sumOneHundred([year1PaymentRatio, year2PaymentRatio, year3PaymentRatio]),
            ]}
            dependencies={
              divisionType === CommissionDivisionType.THREE_YEARS
                ? [
                    ["entries", index, "year1PaymentRatio"],
                    ["entries", index, "year3PaymentRatio"],
                  ]
                : [["entries", index, "year1PaymentRatio"]]
            }
          >
            <InputNumber type="decimal" addon="percentage" />
          </Form.Item>
        </Col>
      ) : undefined}

      {divisionType === CommissionDivisionType.THREE_YEARS ? (
        <Col span={6}>
          <Form.Item
            name={[index, "year3PaymentRatio"]}
            label={t("commissions.productCommissionsSettings.attrs.year3PaymentRatio")}
            rules={[
              validations.notNull,
              validations.minNumber(0.01),
              validations.maxNumber(100),
              validations.sumOneHundred([year1PaymentRatio, year2PaymentRatio, year3PaymentRatio]),
            ]}
            dependencies={[
              ["entries", index, "year1PaymentRatio"],
              ["entries", index, "year2PaymentRatio"],
            ]}
          >
            <InputNumber type="decimal" addon="percentage" />
          </Form.Item>
        </Col>
      ) : undefined}
    </Row>
  );
};

const getCoefficientLabel = (
  businessCoefficient: CreateUpdateProductCommissionsSettings["businessCoefficients"][number],
  t: TFunction,
) => {
  const { valueFrom, valueTo, coefficient } = businessCoefficient;

  const from = valueFrom ? `${t("common.from")}: ${valueFrom}` : "";
  const to = valueTo ? `${t("common.to")}: ${valueTo}` : "";
  const hasRange = from && to;

  const base = `${t("commissions.productCommissionsSettings.attrs.businessCoefficients.coefficient")}: ${formatLocaleNumber(coefficient, 6)}`;
  const range = from || to ? `| ${from}${hasRange ? " - " : ""}${to}` : "";

  return `${base} ${range}`;
};

type BusinessCoefficientsFormItems = {
  coefficientsFieldData: FormListFieldData[];
  businessCoefficients?: CreateUpdateProductCommissionsSettings["businessCoefficients"];
  remove: (index: number) => void;
  commissionsSettingsIndex: number;
};

const BusinessCoefficientsFormItems = ({
  coefficientsFieldData,
  businessCoefficients,
  remove,
  commissionsSettingsIndex,
}: BusinessCoefficientsFormItems) => {
  const { t } = useTranslation();

  const isOnlyOneField = coefficientsFieldData.length === 1;

  const form = Form.useFormInstance();

  const handleValueToChange = (valueTo: number | string, currentIndex: number) => {
    const businessCoefficientsData = [
      ...form.getFieldValue(["entries", commissionsSettingsIndex, "businessCoefficients"]),
    ] as CreateUpdateProductCommissionsSettings["businessCoefficients"];

    if (businessCoefficientsData && businessCoefficientsData.length > currentIndex + 1) {
      businessCoefficientsData[currentIndex + 1] = {
        ...businessCoefficientsData[currentIndex + 1],
        valueFrom: numberOrZero(valueTo) + 1,
      } as CreateUpdateProductCommissionsSettings["businessCoefficients"][number];
    }

    form.setFieldValue(["entries", commissionsSettingsIndex, "businessCoefficients"], businessCoefficientsData);
  };

  const handleRemoveBusinessCoefficient = (indexToRemove: number, remove: (index: number) => void) => {
    if (!businessCoefficients) {
      return;
    }

    const remainingBusinessCoefficients = businessCoefficients.filter((_, index) => index !== indexToRemove)[0];

    remove(indexToRemove);

    if (businessCoefficients.length === 2) {
      form.setFieldValue(
        ["entries", commissionsSettingsIndex, "businessCoefficients"],
        [
          {
            ...remainingBusinessCoefficients,
            valueFrom: undefined,
            valueTo: undefined,
          },
        ],
      );
    }
  };

  const items = coefficientsFieldData.map(({ key, ...field }) => {
    const valueFrom = businessCoefficients?.[field.name]?.valueFrom;
    const businessCoefficient = businessCoefficients?.[field.name];

    return {
      key,
      label:
        businessCoefficient && isDefined(businessCoefficient.coefficient)
          ? getCoefficientLabel(businessCoefficient, t)
          : `${t("commissions.productCommissionsSettings.attrs.businessCoefficients.coefficient")}: -`,
      children: (
        <Row gutter={rowGutter}>
          <HiddenInput name={[field.name, "id"]} />
          <HiddenInput name={[field.name, "optimisticLockVersion"]} />

          <Col span={6}>
            <Form.Item
              name={[field.name, "valueFrom"]}
              label={t("commissions.productCommissionsSettings.attrs.businessCoefficients.valueFrom")}
              rules={[isOnlyOneField ? validations.none : validations.notNull, validations.minNumber(0)]}
            >
              <InputNumber disabled={isOnlyOneField || field.name > 0} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name={[field.name, "valueTo"]}
              label={t("commissions.productCommissionsSettings.attrs.businessCoefficients.valueTo")}
              rules={[
                isOnlyOneField ? validations.none : validations.notNull,
                validations.minNumber(numberOrZero(valueFrom)),
              ]}
            >
              <InputNumber
                min={numberOrZero(valueFrom)}
                disabled={isOnlyOneField}
                onChange={(value) => (value ? handleValueToChange(value, field.name) : undefined)}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name={[field.name, "coefficient"]}
              label={t("commissions.productCommissionsSettings.attrs.businessCoefficients.coefficient")}
              rules={[validations.notNull, validations.minNumber(0.000001)]}
            >
              <InputNumber type="decimal" precision={6} />
            </Form.Item>
          </Col>
        </Row>
      ),
      extra: <AntIcon type="delete" onClick={() => handleRemoveBusinessCoefficient(field.name, remove)} />,
      forceRender: true,
    };
  });

  return <Collapse className="margin-right-tiny" items={items} />;
};

type ProductCommissionsSettingsFormItems = {
  commissionsSettings: (CreateUpdateProductCommissionsSettings & { index: number })[];
  remove: (index: number) => void;
};

const ProductCommissionsSettingsFormItems = ({ commissionsSettings, remove }: ProductCommissionsSettingsFormItems) => {
  const { t } = useTranslation();

  const form = Form.useFormInstance();

  const handleEndDateChange = (endDate: Dayjs, nextIndex?: number) => {
    const commissionsSettingsFormValue = [...form.getFieldValue("entries")] as CreateUpdateProductCommissionsSettings[];

    if (nextIndex && commissionsSettingsFormValue.length > nextIndex) {
      commissionsSettingsFormValue[nextIndex] = {
        ...commissionsSettingsFormValue[nextIndex],
        startDate: dateToIsoDateString(dayjs(endDate).add(1, "days")),
      } as CreateUpdateProductCommissionsSettings;

      form.setFieldsValue({ entries: commissionsSettingsFormValue });
    }
  };

  const handleAddBusinessCoefficient = (index: number, add: (defaultValue?: any) => void) => {
    const businessCoefficients = commissionsSettings.find((item) => item.index === index)?.businessCoefficients;
    const lastValueTo = businessCoefficients?.[businessCoefficients.length - 1]?.valueTo;

    add({ valueFrom: lastValueTo ? lastValueTo + 1 : undefined });
  };

  const items = commissionsSettings.map(({ index, ...data }, i) => {
    const isNotOnlyOneAndLastField = commissionsSettings.length > 1 && i < commissionsSettings.length - 1;

    return {
      key: data.id,
      label: getProductCommissionsSettingsLabel(data, t),
      children: (
        <div className="margin-bottom-medium">
          <HiddenInput name={[index, "id"]} />
          <HiddenInput name={[index, "optimisticLockVersion"]} />
          <HiddenInput name={[index, "commissionKind"]} />

          <Row gutter={rowGutter}>
            <Col span={6}>
              <Form.Item
                name={[index, "startDate"]}
                label={t("commissions.productCommissionsSettings.attrs.startDate")}
                rules={[validations.notNull]}
                {...datePickerFormItemProps}
              >
                <DatePicker {...datePickerStandardProps} disabled={i >= 1} />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                name={[index, "endDate"]}
                label={t("commissions.productCommissionsSettings.attrs.endDate")}
                rules={[
                  isNotOnlyOneAndLastField ? validations.notNull : validations.none,
                  validations.notBefore(data.startDate, t("commissions.productCommissionsSettings.attrs.startDate")),
                ]}
                {...datePickerFormItemProps}
              >
                <DatePicker<Dayjs>
                  {...datePickerStandardProps}
                  defaultPickerValue={toDate(data.endDate || data.startDate)}
                  allowClear={!isNotOnlyOneAndLastField}
                  minDate={dayjs(data.startDate)}
                  onChange={(value) =>
                    value ? handleEndDateChange(value, commissionsSettings[i + 1]?.index) : undefined
                  }
                />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                name={[index, "amountAdjustmentRatio"]}
                label={
                  <LabelWithTooltip
                    tooltip={t("commissions.productCommissionsSettings.helpers.amountAdjustmentRatioHint")}
                    label={t("commissions.productCommissionsSettings.attrs.amountAdjustmentRatio")}
                  />
                }
                rules={[validations.notNull, validations.minNumber(0.0001), validations.maxNumber(200)]}
              >
                <InputNumber type="decimal" precision={4} step={0.0001} addon="percentage" />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                name={[index, "pointsCoefficient"]}
                label={t("commissions.productCommissionsSettings.attrs.pointsCoefficient")}
                rules={[validations.notNull, validations.minNumber(0.01)]}
              >
                <InputNumber type="decimal" />
              </Form.Item>
            </Col>
          </Row>

          <DivisionTypeFormItems
            index={index}
            divisionType={data.divisionType}
            year1PaymentRatio={data.year1PaymentRatio}
            year2PaymentRatio={data.year2PaymentRatio}
            year3PaymentRatio={data.year3PaymentRatio}
          />

          <Row gutter={rowGutter}>
            <Col span={6}>
              <Form.Item
                name={[index, "directPointsAmount"]}
                label={
                  <LabelWithTooltip
                    tooltip={t("commissions.productCommissionsSettings.helpers.directPointsAmountHint")}
                    label={t("commissions.productCommissionsSettings.attrs.directPointsAmount")}
                  />
                }
                rules={[validations.minNumber(0)]}
              >
                <InputNumber type="decimal" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name={[index, "directPaymentAmount"]}
                label={
                  <LabelWithTooltip
                    tooltip={t("commissions.productCommissionsSettings.helpers.directPaymentAmountHint")}
                    label={t("commissions.productCommissionsSettings.attrs.directPaymentAmount")}
                  />
                }
                rules={[
                  validations.minNumber(0),
                  validations.nullIfOtherNotNull(
                    ["entries", index, "directPaymentRatio"],
                    t("commissions.productCommissionsSettings.attrs.directPaymentRatio"),
                  ),
                ]}
                dependencies={[["entries", index, "directPaymentRatio"]]}
              >
                <InputNumber type="decimal" addon="euro" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name={[index, "directPaymentRatio"]}
                label={
                  <LabelWithTooltip
                    tooltip={t("commissions.productCommissionsSettings.helpers.directPaymentRatioHint")}
                    label={t("commissions.productCommissionsSettings.attrs.directPaymentRatio")}
                  />
                }
                rules={[
                  validations.minNumber(0.0001),
                  validations.maxNumber(200),
                  validations.nullIfOtherNotNull(
                    ["entries", index, "directPaymentAmount"],
                    t("commissions.productCommissionsSettings.attrs.directPaymentAmount"),
                  ),
                ]}
                dependencies={[["entries", index, "directPaymentAmount"]]}
              >
                <InputNumber type="decimal" precision={4} step={0.0001} addon="percentage" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name={[index, "omitManagerialCommissions"]}
                className="form-item-without-label"
                valuePropName="checked"
                initialValue={false}
                rules={[validations.notNull]}
              >
                <Checkbox>{t("commissions.productCommissionsSettings.attrs.omitManagerialCommissions")}</Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Divider titlePlacement="start">
            {t("commissions.productCommissionsSettings.sections.businessCoefficients")}
          </Divider>

          <Form.List name={[index, "businessCoefficients"]} initialValue={[]}>
            {(coefficientsFieldData, { add: addCoefficient, remove: removeCoefficient }) => (
              <>
                <div
                  style={{
                    overflowY: "auto",
                    maxHeight: 250,
                    display: coefficientsFieldData.length ? "block" : "none",
                  }}
                >
                  <BusinessCoefficientsFormItems
                    coefficientsFieldData={coefficientsFieldData}
                    businessCoefficients={data.businessCoefficients}
                    remove={removeCoefficient}
                    commissionsSettingsIndex={index}
                  />
                </div>

                <ActionButton
                  icon="plus"
                  onClick={() => handleAddBusinessCoefficient(index, addCoefficient)}
                  className="margin-top-small"
                >
                  {t("commissions.productCommissionsSettings.helpers.addCoefficient")}
                </ActionButton>
              </>
            )}
          </Form.List>
        </div>
      ),
      extra: commissionsSettings.length > 1 ? <AntIcon type="delete" onClick={() => remove(index)} /> : undefined,
      forceRender: true,
    };
  });

  return <Collapse style={{ display: items.length ? "block" : "none" }} className="margin-right-tiny" items={items} />;
};

type Props = {
  form: FormInstance<CreateUpdateEntries<CreateUpdateProductCommissionsSettings>>;
  coveredByNbs?: boolean;
};

export const ProductCommissionsSettingsForm = ({ form, coveredByNbs }: Props) => {
  const { t } = useTranslation();

  const [activeTabKey, setActiveTabKey] = useState<CommissionKind>();
  const [isOpenCommissionKindTabModal, setIsOpenCommissionKindTabModal] = useState(false);

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const entries = Form.useWatch("entries", { form, preserve: true });

  const tabItems = [...new Set(entries?.map((item) => item.commissionKind))];

  // The active tab can also disappear when the non-initial kinds are removed below; the last tab takes its place
  const currentTabKey =
    tabItems.length === 1
      ? tabItems[0]
      : activeTabKey && !tabItems.includes(activeTabKey)
        ? tabItems[tabItems.length - 1]
        : activeTabKey;

  useEffect(() => {
    if (!coveredByNbs && isTopAgentPlus) {
      const initialEntries = entries?.filter(
        (item) => getCommissionKindCategory(item.commissionKind) === CommissionKindCategory.INITIAL,
      );

      if (initialEntries && initialEntries.length !== entries.length) {
        form.setFieldsValue({ entries: initialEntries });
      }
    }
  }, [coveredByNbs, isTopAgentPlus, entries]);

  const removeCommissionKindTab = (targetKey: TargetKey) => {
    const targetIndex = tabItems.findIndex((item) => item === targetKey);
    const newItems = tabItems.filter((item) => item !== targetKey);

    if (newItems.length && targetKey === currentTabKey) {
      const newActiveKey = newItems[targetIndex === newItems.length ? targetIndex - 1 : targetIndex];
      setActiveTabKey(newActiveKey);
    }

    const finalCommissionsSettings = entries.filter((item) => item.commissionKind !== targetKey);

    form.setFieldsValue({ entries: finalCommissionsSettings });
  };

  const onEditTab = (targetKey: TargetKey, action: "add" | "remove") => {
    if (action === "add") {
      setIsOpenCommissionKindTabModal(true);
    } else {
      removeCommissionKindTab(targetKey);
    }
  };

  const addDataToCommissionsSettingsList = (
    commissionKind: CommissionKind,
    add: (defaultValue?: any, insertIndex?: number) => void,
  ) => {
    const commissionKindEntries = entries?.filter((item) => item.commissionKind === commissionKind);
    const lastEndDate = commissionKindEntries[commissionKindEntries.length - 1]?.endDate;

    add({
      startDate: lastEndDate ? dateToIsoDateString(dayjs(lastEndDate).add(1, "days")) : undefined,
      commissionKind,
    });
  };

  return (
    <>
      <Divider titlePlacement="start">
        {t("commissions.productCommissionsSettings.sections.commissionSettings")}
      </Divider>

      <Form form={form} layout="vertical" name="productCommissionsSettingsForm">
        <Form.List name="entries" initialValue={[]}>
          {(commissionsSettingsFieldData, { add, remove }) => (
            <>
              <Tabs
                type="editable-card"
                activeKey={currentTabKey}
                onEdit={onEditTab}
                onChange={(key) => setActiveTabKey(key as CommissionKind)}
                items={tabItems.map((tabItem) => ({
                  key: tabItem,
                  label: getCommissionKindTabLabel(tabItem, t),
                  forceRender: true,
                  children: (
                    <>
                      <div style={{ overflowY: "auto", maxHeight: 800 }}>
                        <ProductCommissionsSettingsFormItems
                          commissionsSettings={commissionsSettingsFieldData
                            .map(({ name }) => {
                              const data = entries[name];

                              return data ? { ...data, index: name } : undefined;
                            })
                            .filter((item) => !!item)
                            .filter((item) => item?.commissionKind === tabItem)}
                          remove={remove}
                        />
                      </div>

                      <ActionButton
                        icon="plus"
                        onClick={() => addDataToCommissionsSettingsList(tabItem, add)}
                        className="margin-top-small"
                      >
                        {t("commissions.productCommissionsSettings.helpers.addProductCommissionSettings")}
                      </ActionButton>
                    </>
                  ),
                }))}
              />

              <AddCommissionKindTabModal
                onSave={(tab) => {
                  setIsOpenCommissionKindTabModal(false);
                  setActiveTabKey(tab);
                  addDataToCommissionsSettingsList(tab, add);
                }}
                isOpen={isOpenCommissionKindTabModal}
                excluded={[
                  ...tabItems,
                  ...(coveredByNbs
                    ? []
                    : Object.values(CommissionKind).filter(
                        (item) => getCommissionKindCategory(item) !== CommissionKindCategory.INITIAL,
                      )),
                ]}
                onCancel={() => setIsOpenCommissionKindTabModal(false)}
              />
            </>
          )}
        </Form.List>
      </Form>
    </>
  );
};
